import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { FormField } from '@/components/ui/form-field'
import { Input } from '@/components/ui/input'
import apiClient from '@/lib/apiClient'
import { cn } from '@/lib/utils'
import { zodResolver } from '@hookform/resolvers/zod'
import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

const categoryFormSchema = z.object({
  name: z.string().min(1, 'Category name is required'),
  description: z.string().optional(),
  status: z.enum(['active', 'inactive']),
  sortOrder: z.number().min(0),
  parentId: z.string().optional(),
  seoTitle: z.string().optional(),
  seoDescription: z.string().optional(),
  seoKeywords: z.string().optional(),
})

type CategoryFormData = z.infer<typeof categoryFormSchema>

interface CategoryOption {
  id: string
  name: string
  level: number
  fullPath: string
}

interface CategoryFormProps {
  className?: string
  initialData?: Partial<CategoryFormData>
  onSubmit: (_data: CategoryFormData) => void
  onCancel: () => void
  isLoading?: boolean
  editingCategoryId?: string
}

export const CategoryForm: React.FC<CategoryFormProps> = ({
  className,
  initialData,
  onSubmit,
  onCancel,
  isLoading = false,
  editingCategoryId,
}) => {
  const [categories, setCategories] = useState<CategoryOption[]>([])
  const [isLoadingCategories, setIsLoadingCategories] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
    setFocus,
  } = useForm<CategoryFormData>({
    resolver: zodResolver(categoryFormSchema),
    mode: 'onChange', // Add this for real-time validation
    defaultValues: {
      name: '',
      description: '',
      status: 'active',
      sortOrder: 0,
      parentId: '',
      seoTitle: '',
      seoDescription: '',
      seoKeywords: '',
      ...initialData,
    },
  })

  // Load categories for parent selection
  useEffect(() => {
    const loadCategories = async () => {
      setIsLoadingCategories(true)
      try {
        const response = await apiClient.getCategories({ per_page: 1000 })
        const apiCategories = response.data || []

        // Build hierarchical options
        const buildCategoryOptions = (
          cats: Array<{
            id: string
            name: string
            parent_id?: string
            children?: Array<{
              id: string
              name: string
              parent_id?: string
              children?: unknown
            }>
          }>,
          level = 0,
          parentPath = ''
        ): CategoryOption[] => {
          const options: CategoryOption[] = []

          cats.forEach((cat) => {
            // Skip the category being edited and its descendants to prevent circular references
            if (
              editingCategoryId &&
              (cat.id === editingCategoryId || isDescendant(cat, editingCategoryId, cats))
            ) {
              return
            }

            const fullPath = parentPath ? `${parentPath} > ${cat.name}` : cat.name
            options.push({
              id: cat.id,
              name: cat.name,
              level,
              fullPath,
            })

            // Add children
            if (cat.children && cat.children.length > 0) {
              options.push(
                ...buildCategoryOptions(
                  cat.children as Array<{
                    id: string
                    name: string
                    parent_id?: string
                    children?: Array<{
                      id: string
                      name: string
                      parent_id?: string
                      children?: unknown
                    }>
                  }>,
                  level + 1,
                  fullPath
                )
              )
            }
          })

          return options
        }

        const isDescendant = (
          category: { parent_id?: string },
          ancestorId: string,
          allCategories: Array<{ id: string; parent_id?: string }>
        ): boolean => {
          if (!category.parent_id) return false
          if (category.parent_id === ancestorId) return true

          const parent = allCategories.find((c) => c.id === category.parent_id)
          return parent ? isDescendant(parent, ancestorId, allCategories) : false
        }

        const options = buildCategoryOptions(apiCategories)
        setCategories(options)
      } catch (error) {
        console.error('Failed to load categories:', error)
      } finally {
        setIsLoadingCategories(false)
      }
    }

    loadCategories()
  }, [editingCategoryId])

  const onFormSubmit = (data: CategoryFormData) => {
    onSubmit(data)
  }

  const onFormError = () => {
    // Focus on the first invalid field
    if (errors.name) {
      setFocus('name')
    } else if (errors.description) {
      setFocus('description')
    } else if (errors.parentId) {
      setFocus('parentId')
    } else if (errors.status) {
      setFocus('status')
    } else if (errors.sortOrder) {
      setFocus('sortOrder')
    } else if (errors.seoTitle) {
      setFocus('seoTitle')
    } else if (errors.seoDescription) {
      setFocus('seoDescription')
    } else if (errors.seoKeywords) {
      setFocus('seoKeywords')
    }
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>{initialData ? 'Edit Category' : 'Add Category'}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onFormSubmit, onFormError)} className="space-y-4">
          {/* Basic Information */}
          <div className="space-y-4">
            <FormField id="name" label="Category Name" error={!!errors.name} required>
              <Input {...register('name', { required: true })} placeholder="Enter category name" />
            </FormField>

            <FormField id="description" label="Description" error={!!errors.description}>
              <textarea
                {...register('description')}
                placeholder="Enter category description"
                className={cn(
                  'min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
                  errors.description && '!border-red-500 !focus-visible:ring-red-500'
                )}
              />
            </FormField>

            <FormField id="parentId" label="Parent Category" error={!!errors.parentId}>
              <div className="relative">
                <select
                  {...register('parentId')}
                  className={cn(
                    'flex h-10 w-full rounded-md border border-input bg-muted/60 px-3 py-2 pr-10 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 appearance-none cursor-pointer',
                    errors.parentId && '!border-red-500 !focus-visible:ring-red-500'
                  )}
                  disabled={isLoadingCategories}
                >
                  <option value="">None (Root Category)</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {'  '.repeat(category.level) +
                        (category.level > 0 ? '└ ' : '') +
                        category.fullPath}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                  <svg
                    className="fill-current h-4 w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                  </svg>
                </div>
              </div>
            </FormField>

            <FormField id="status" label="Status" error={!!errors.status}>
              <div className="relative">
                <select
                  {...register('status')}
                  className={cn(
                    'flex h-10 w-full rounded-md border border-input bg-muted/60 px-3 py-2 pr-10 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 appearance-none cursor-pointer',
                    errors.status && '!border-red-500 !focus-visible:ring-red-500'
                  )}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                  <svg
                    className="fill-current h-4 w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                  </svg>
                </div>
              </div>
            </FormField>

            <FormField id="sortOrder" label="Sort Order" error={!!errors.sortOrder}>
              <Input
                type="number"
                {...register('sortOrder', { valueAsNumber: true })}
                placeholder="0"
              />
            </FormField>
          </div>

          {/* SEO Fields */}
          <div className="border-t pt-4">
            <h3 className="text-lg font-medium mb-4">SEO Settings</h3>
            <div className="space-y-4">
              <FormField id="seoTitle" label="SEO Title" error={!!errors.seoTitle}>
                <Input {...register('seoTitle')} placeholder="SEO title" />
              </FormField>

              <FormField
                id="seoDescription"
                label="SEO Description"
                error={!!errors.seoDescription}
              >
                <textarea
                  {...register('seoDescription')}
                  placeholder="SEO description"
                  className={cn(
                    'min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
                    errors.seoDescription && '!border-red-500 !focus-visible:ring-red-500'
                  )}
                />
              </FormField>

              <FormField id="seoKeywords" label="SEO Keywords" error={!!errors.seoKeywords}>
                <Input
                  {...register('seoKeywords')}
                  placeholder="water pumps, submersible, centrifugal"
                />
              </FormField>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Saving...' : initialData ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}
